import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import { FILE_TYPE_CATEGORIES, FileTypeCategory } from '../../../../support/campaigns';

/**
 * The "File uploads" section sub-form for a content-bearing step (content / action_item / followup).
 * A pure controlled sub-form binding the parent form's `control`:
 *
 *   - `file_upload_enabled` (bool)         — the on/off toggle; presence of `metadata.fileUpload`.
 *   - `file_upload_title` (string)         — files-section heading.
 *   - `file_upload_description` (string)   — files-section prose (why the files are needed).
 *   - `file_upload_types` (FileTypeCategory[]) — allowed file types; "Any" is exclusive.
 *   - `file_upload_required` (bool)        — require ≥1 upload before the step can complete.
 *
 * When the toggle is off the config fields are hidden and StepDetailForm drops `metadata.fileUpload`
 * on save, so the runner renders no uploader.
 */
export function FileUploadFields(props: {
  control: Control<any>;
  disabled?: boolean;
}): JSX.Element {
  const { control, disabled } = props;
  const labelFor = (v: FileTypeCategory) =>
    FILE_TYPE_CATEGORIES.find((c) => c.value === v)?.label ?? v;

  return (
    <Stack spacing={2}>
      <Controller
        name="file_upload_enabled"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                disabled={disabled}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Collect file uploads on this step
                </Box>
                <Box
                  component="span"
                  sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Adds an uploader to this step. Files are stored per delivery and only people with
                  access to the campaign can download them.
                </Box>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        )}
      />

      <Controller
        name="file_upload_enabled"
        control={control}
        render={({ field: enabledField }) =>
          enabledField.value ? (
            <Stack spacing={2}>
              <Controller
                name="file_upload_title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Files section title"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    helperText="Heading shown above the uploader — what files this step collects. Defaults to “File uploads”."
                  />
                )}
              />
              <Controller
                name="file_upload_description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Files section description"
                    size="small"
                    multiline
                    minRows={2}
                    fullWidth
                    disabled={disabled}
                    helperText="Context for the uploader — why these files are needed. Markdown supported."
                  />
                )}
              />
              <Controller
                name="file_upload_types"
                control={control}
                render={({ field }) => {
                  const selected: FileTypeCategory[] = Array.isArray(field.value)
                    ? (field.value as FileTypeCategory[])
                    : ['any'];
                  const onChange = (next: FileTypeCategory[]) => {
                    // "Any" is exclusive: picking it clears the specific categories, and picking a
                    // specific category clears "Any" — so the two can never contradict each other.
                    const added = next.find((v) => !selected.includes(v));
                    let resolved: FileTypeCategory[];
                    if (added === 'any') resolved = ['any'];
                    else if (added) resolved = next.filter((v) => v !== 'any');
                    else resolved = next;
                    field.onChange(resolved.length ? Array.from(new Set(resolved)) : ['any']);
                  };
                  return (
                    <FormControl size="small" fullWidth disabled={disabled}>
                      <InputLabel id="file-upload-types-label">Allowed file types</InputLabel>
                      <Select
                        labelId="file-upload-types-label"
                        multiple
                        value={selected}
                        input={<OutlinedInput label="Allowed file types" />}
                        onChange={(e) =>
                          onChange(
                            (typeof e.target.value === 'string'
                              ? e.target.value.split(',')
                              : e.target.value) as FileTypeCategory[],
                          )
                        }
                        renderValue={(vals) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(vals as FileTypeCategory[]).map((v) => (
                              <Chip key={v} size="small" label={labelFor(v)} />
                            ))}
                          </Box>
                        )}
                      >
                        {FILE_TYPE_CATEGORIES.map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            <Checkbox checked={selected.includes(c.value)} />
                            <ListItemText primary={c.label} secondary={c.help} />
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Restrict uploads to these file types. “Any file type” allows everything.
                      </Typography>
                    </FormControl>
                  );
                }}
              />
              <Controller
                name="file_upload_required"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        disabled={disabled}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Require a file to complete this step
                        </Box>
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                        >
                          The SE can’t advance past this step until at least one file is uploaded for
                          the delivery. Leave off to make the uploader optional.
                        </Box>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                )}
              />
            </Stack>
          ) : (
            <></>
          )
        }
      />
    </Stack>
  );
}

export default FileUploadFields;
